import React, { useEffect, useState } from "react";
import { copyText } from "@/lib/clipboard";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, ShieldCheck, Star, Trash2, Copy, Globe, CheckCircle2, Clock, XCircle } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth, hasPerm } from "@/context/AuthContext";
import { Toolbar } from "@/components/kit/Toolbar";
import { DataTable } from "@/components/kit/DataTable";
import { FormDrawer } from "@/components/kit/FormDrawer";
import { TextField, Field } from "@/components/kit/fields";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { fmtDate } from "@/lib/format";
import { cn } from "@/lib/utils";

const USAGE_OPTS = [
  { value: "partner_panel", label: "Partner Portal", desc: "Affiliate login, registration and password recovery.", color: "#22C55E" },
  { value: "tracking", label: "Tracking", desc: "Click tracking links and redirects.", color: "#EAB308" },
  { value: "postback", label: "Postback", desc: "Advertiser / server-to-server conversion callbacks.", color: "#A855F7" },
];
const USAGE_LABEL = Object.fromEntries(USAGE_OPTS.map((o) => [o.value, o.label]));
const USAGE_COLOR = Object.fromEntries(USAGE_OPTS.map((o) => [o.value, o.color]));

function cnameParts(domain) {
  const d = (domain || "").trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/.*$/, "");
  const labels = d.split(".").filter(Boolean);
  if (labels.length <= 2) return { host: "@", root: d, isRoot: labels.length === 2, valid: labels.length >= 1 };
  return { host: labels.slice(0, labels.length - 2).join("."), root: labels.slice(-2).join("."), isRoot: false, valid: true };
}
const STATUS = {
  active: { label: "Active", cls: "text-[#22C55E]", icon: CheckCircle2 },
  pending_dns: { label: "Pending DNS", cls: "text-[#EAB308]", icon: Clock },
  pending: { label: "Pending", cls: "text-[#EAB308]", icon: Clock },
  failed: { label: "Failed", cls: "text-[#EF4444]", icon: XCircle },
  disabled: { label: "Disabled", cls: "text-muted-foreground", icon: XCircle },
};
const Badge = ({ s }) => {
  const m = STATUS[s] || STATUS.pending;
  return <span className={cn("inline-flex items-center gap-1.5 text-xs font-medium", m.cls)}><m.icon className="h-3.5 w-3.5" />{m.label}</span>;
};
const usagesOf = (d) => d.usages || (d.type ? [d.type] : []);

function UsagePills({ usages, defaults }) {
  const list = usages || [];
  if (!list.length) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="flex flex-wrap gap-1.5">
      {list.map((u) => (
        <span key={u} className="inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] font-medium"
          style={{ color: USAGE_COLOR[u], borderColor: `${USAGE_COLOR[u]}44` }}>
          {(defaults || {})[u] && <Star className="h-3 w-3 fill-current" />}
          {USAGE_LABEL[u] || u}
        </span>
      ))}
    </span>
  );
}

export function DomainsManager() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [drawer, setDrawer] = useState({ open: false, mode: "add", item: null });
  const { data } = useQuery({ queryKey: ["domains"], queryFn: async () => (await http.get("/domains")).data });
  const { data: platform } = useQuery({ queryKey: ["platform-public"], queryFn: async () => (await http.get("/platform-settings/public")).data });
  const rows = data?.items || [];
  const allowPortal = data?.allow_portal_domain !== false;
  const inv = () => qc.invalidateQueries({ queryKey: ["domains"] });

  const verify = useMutation({ mutationFn: (id) => http.post(`/domains/${id}/verify`), onSuccess: inv });
  const del = useMutation({ mutationFn: (id) => http.delete(`/domains/${id}`), onSuccess: inv });
  const create = useMutation({ mutationFn: (body) => http.post("/domains", body), onSuccess: inv });
  const update = useMutation({ mutationFn: ({ id, body }) => http.put(`/domains/${id}`, body), onSuccess: inv });
  const [tdDefault, setTdDefault] = useState(null); // { domain, mode }

  const canEdit = hasPerm(user, "domains.edit");
  const isTrackingDomain = (r) => usagesOf(r).includes("tracking") && (r.status === "active" || r.verification_status === "verified") && !(r.defaults || {}).tracking;
  const applyTdDefault = async () => {
    try {
      const { data: res } = await http.post(`/domains/${tdDefault.domain.id}/make-tracking-default`, { mode: tdDefault.mode });
      toast.success(tdDefault.mode === "org_wide"
        ? `Default set · ${res.offers_updated} offer${res.offers_updated === 1 ? "" : "s"} updated`
        : "Default tracking domain set for new GEO offers");
      setTdDefault(null); inv();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const columns = [
    { key: "domain", label: "Domain", render: (r) => <span className="inline-flex items-center gap-2 font-medium text-foreground"><Globe className="h-3.5 w-3.5 text-muted-foreground" />{r.domain}</span> },
    { key: "usages", label: "Used for", render: (r) => <UsagePills usages={usagesOf(r)} defaults={r.defaults} /> },
    { key: "status", label: "Status", render: (r) => <Badge s={r.status} /> },
    { key: "ssl_status", label: "SSL", render: (r) => <Badge s={r.ssl_status} /> },
    { key: "created_at", label: "Created", render: (r) => fmtDate(r.created_at) },
  ];

  const rowActions = (r) => [
    { label: "View / Verify", icon: ShieldCheck, onClick: () => setDrawer({ open: true, mode: "view", item: r }), testId: `domain-view-${r.id}` },
    ...(isTrackingDomain(r) ? [{ label: "Make default tracking domain", icon: Star, onClick: () => setTdDefault({ domain: r, mode: "new_offers" }), testId: `domain-make-td-${r.id}` }] : []),
    ...(hasPerm(user, "domains.delete") ? [{ label: "Delete", icon: Trash2, danger: true, onClick: () => window.confirm(`Delete ${r.domain}?`) && del.mutate(r.id), testId: `domain-delete-${r.id}` }] : []),
  ];

  return (
    <div className="space-y-4" data-testid="domains-manager">
      <p className="max-w-2xl text-sm text-muted-foreground">Connect your own domains via a CNAME record. A single domain can serve the Partner Portal, Tracking and Postbacks at once — pick the usages below. Verification &amp; SSL are issued automatically.</p>
      <Toolbar onAdd={hasPerm(user, "domains.create") ? () => setDrawer({ open: true, mode: "add", item: null }) : null} addLabel="Add Domain" addTestId="add-domain-btn" />
      <DataTable testId="domains-table" columns={columns} rows={rows}
        onRowClick={(r) => setDrawer({ open: true, mode: "view", item: r })}
        rowActions={canEdit ? rowActions : null} emptyText="No domains connected yet." />

      <AddDomainDrawer open={drawer.open && drawer.mode === "add"} cname={platform?.cname_target} allowPortal={allowPortal}
        onOpenChange={(o) => setDrawer((d) => ({ ...d, open: o }))}
        onCreate={async (body) => { const r = await create.mutateAsync(body); toast.success("Domain added — verify DNS to activate"); setDrawer({ open: true, mode: "view", item: r.data }); }} />
      <DomainDetailDrawer open={drawer.open && drawer.mode === "view"} domain={rows.find((x) => x.id === drawer.item?.id) || drawer.item} allowPortal={allowPortal}
        cname={platform?.cname_target} previewHost={platform?.dev_fallback_domain} canEdit={canEdit}
        onOpenChange={(o) => setDrawer((d) => ({ ...d, open: o }))}
        onVerify={async () => { const r = await verify.mutateAsync(drawer.item.id); toast[r.data.status === "active" ? "success" : "error"](r.data.message); }}
        onSave={async (body) => { await update.mutateAsync({ id: drawer.item.id, body }); toast.success("Domain usages updated"); }} />

      <Dialog open={!!tdDefault} onOpenChange={(o) => { if (!o) setTdDefault(null); }}>
        <DialogContent className="border-white/[0.1] bg-[#141417]" data-testid="td-default-dialog">
          <DialogHeader>
            <DialogTitle>Make default tracking domain</DialogTitle>
            <DialogDescription>Choose how <b>{tdDefault?.domain?.domain}</b> becomes your default tracking domain.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {[["new_offers", "Only for new GEO Offers", "New offers inherit this domain. Existing offers and streams are not changed. (Safe)"],
              ["org_wide", "For the entire organization", "Repoint every existing GEO Offer that used the old default to this domain. Streams that inherited it follow automatically; manual overrides stay."]].map(([v, t, d]) => (
              <label key={v} className={cn("block cursor-pointer rounded-lg border p-3", tdDefault?.mode === v ? "border-white/30 bg-white/[0.04]" : "border-white/10")}>
                <div className="flex items-center gap-2">
                  <input type="radio" name="td-mode" checked={tdDefault?.mode === v} onChange={() => setTdDefault((s) => ({ ...s, mode: v }))} data-testid={`td-mode-${v}`} className="accent-white" />
                  <span className="text-sm font-medium text-foreground">{t}</span>
                </div>
                <p className="mt-1 pl-6 text-xs text-muted-foreground">{d}</p>
              </label>
            ))}
            {tdDefault?.mode === "org_wide" && (
              <p className="rounded-md border border-[#EAB308]/30 bg-[#EAB308]/10 p-2.5 text-xs text-[#EAB308]" data-testid="td-org-warning">
                This will update all GEO Offers and related Streams currently using the default domain.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTdDefault(null)} data-testid="td-cancel" className="border-white/15">Cancel</Button>
            <Button onClick={applyTdDefault} data-testid="td-confirm" className="bg-white text-black hover:bg-white/90">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function UsageChecklist({ usages, defaults, setUsages, setDefaults, idPrefix, allowPortal = true }) {
  const toggle = (u) => setUsages((s) => ({ ...s, [u]: !s[u] }));
  const toggleDef = (u) => setDefaults((s) => ({ ...s, [u]: !s[u] }));
  const opts = allowPortal ? USAGE_OPTS : USAGE_OPTS.filter((o) => o.value !== "partner_panel");
  return (
    <div className="space-y-2.5">
      {opts.map((o) => (
        <div key={o.value} className={cn("rounded-lg border p-3 transition-colors", usages[o.value] ? "border-white/30 bg-white/[0.04]" : "border-white/10")}>
          <button type="button" onClick={() => toggle(o.value)} data-testid={`${idPrefix}-usage-${o.value}`} className="flex w-full items-start gap-3 text-left">
            <span className={cn("mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border", usages[o.value] ? "border-transparent" : "border-white/30")}
              style={usages[o.value] ? { backgroundColor: o.color } : {}}>
              {usages[o.value] && <CheckCircle2 className="h-3.5 w-3.5 text-black" />}
            </span>
            <span className="flex-1">
              <span className="text-sm font-medium" style={{ color: usages[o.value] ? o.color : undefined }}>{o.label}</span>
              <span className="block text-xs text-muted-foreground">{o.desc}</span>
            </span>
          </button>
          {usages[o.value] && (
            <button type="button" onClick={() => toggleDef(o.value)} data-testid={`${idPrefix}-default-${o.value}`}
              className="mt-2 ml-7 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
              <span className={cn("flex h-3.5 w-3.5 items-center justify-center rounded-sm border", defaults[o.value] ? "border-transparent bg-[#EAB308]" : "border-white/30")}>
                {defaults[o.value] && <Star className="h-2.5 w-2.5 fill-black text-black" />}
              </span>
              Make default {o.label} domain
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function AddDomainDrawer({ open, cname, onOpenChange, onCreate, allowPortal = true }) {
  const [domain, setDomain] = useState("");
  const initUsages = () => ({ partner_panel: allowPortal, tracking: !allowPortal, postback: false });
  const [usages, setUsages] = useState(initUsages);
  const [defaults, setDefaults] = useState({ partner_panel: false, tracking: false, postback: false });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setDomain(""); setUsages(initUsages()); setDefaults({ partner_panel: false, tracking: false, postback: false }); } }, [open, allowPortal]);
  const save = async () => {
    const usageList = Object.keys(usages).filter((k) => usages[k] && (allowPortal || k !== "partner_panel"));
    if (!usageList.length) { toast.error("Select at least one usage"); return; }
    setSaving(true);
    try { await onCreate({ domain, usages: usageList, defaults }); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };
  return (
    <FormDrawer open={open} onOpenChange={onOpenChange} title="Add Domain" size="md" onSave={save} saving={saving} saveLabel="Add Domain" testId="add-domain-drawer">
      <div className="space-y-4">
        <TextField label="Domain" value={domain} onChange={setDomain} testId="add-domain-input" placeholder="track.yourdomain.com" />
        <div className="space-y-2">
          <label className="text-xs font-medium text-muted-foreground">Use this domain for</label>
          <UsageChecklist usages={usages} defaults={defaults} setUsages={setUsages} setDefaults={setDefaults} idPrefix="add-domain" allowPortal={allowPortal} />
        </div>
        <CnameInstructions domain={domain} cname={cname} />
      </div>
    </FormDrawer>
  );
}

function DomainDetailDrawer({ open, domain, cname, previewHost, canEdit, onOpenChange, onVerify, onSave, allowPortal = true }) {
  const qc = useQueryClient();
  const [usages, setUsages] = useState({});
  const [defaults, setDefaults] = useState({});
  const [saving, setSaving] = useState(false);
  const [pendingTd, setPendingTd] = useState(null); // mode string while the tracking-default dialog is open
  useEffect(() => {
    if (domain) {
      const u = usagesOf(domain);
      setUsages({ partner_panel: u.includes("partner_panel"), tracking: u.includes("tracking"), postback: u.includes("postback") });
      setDefaults({ ...(domain.defaults || {}) });
    }
  }, [domain]);
  useEffect(() => { if (!open) setPendingTd(null); }, [open]);
  if (!domain) return null;
  const save = async () => {
    const usageList = Object.keys(usages).filter((k) => usages[k]);
    if (!usageList.length) { toast.error("Select at least one usage"); return; }
    const wasTrackingDefault = !!(domain.defaults || {}).tracking;
    const wantsTrackingDefault = !!defaults.tracking && usageList.includes("tracking");
    // Newly turning ON the tenant default tracking domain → ask the scope BEFORE saving anything.
    if (wantsTrackingDefault && !wasTrackingDefault) { setPendingTd("new_offers"); return; }
    setSaving(true);
    try { await onSave({ usages: usageList, defaults }); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };
  // Confirm the tracking-default choice: persist usages/other-defaults (tracking off), then apply
  // the chosen cascade mode atomically. Cancel saves nothing.
  const confirmTd = async () => {
    const usageList = Object.keys(usages).filter((k) => usages[k]);
    setSaving(true);
    try {
      await onSave({ usages: usageList, defaults: { ...defaults, tracking: false } });
      const { data } = await http.post(`/domains/${domain.id}/make-tracking-default`, { mode: pendingTd });
      toast.success(pendingTd === "org_wide"
        ? `Default set · ${data.offers_updated} offer${data.offers_updated === 1 ? "" : "s"} updated`
        : "Default tracking domain set for new GEO offers");
      setPendingTd(null);
      onOpenChange(false);
      qc.invalidateQueries({ queryKey: ["domains"] });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };
  return (
    <>
    <FormDrawer open={open} onOpenChange={onOpenChange} title={domain.domain} subtitle="Custom domain" size="md" hideFooter testId="domain-detail-drawer">
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Status"><Badge s={domain.status} /></Field>
          <Field label="SSL"><Badge s={domain.ssl_status} /></Field>
        </div>
        <div className="space-y-2">
          <label className="text-xs font-medium text-muted-foreground">Used for</label>
          {canEdit
            ? <UsageChecklist usages={usages} defaults={defaults} setUsages={setUsages} setDefaults={setDefaults} idPrefix="edit-domain" allowPortal={allowPortal || usagesOf(domain).includes("partner_panel")} />
            : <UsagePills usages={usagesOf(domain)} defaults={domain.defaults} />}
          {canEdit && <Button onClick={save} disabled={saving} data-testid="domain-usages-save" variant="outline" className="border-white/[0.12]">Save usages</Button>}
        </div>
        <CnameInstructions domain={domain.domain} cname={cname || domain.cname_target} />
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => onVerify()} data-testid="domain-verify-btn" className="gap-2 bg-white text-black hover:bg-white/90"><ShieldCheck className="h-4 w-4" /> Verify DNS</Button>
        </div>
        {domain.last_verify_message && <p className="text-xs text-muted-foreground">Last check: {domain.last_verify_message}</p>}
        <div className="rounded-lg border border-[#EAB308]/20 bg-[#EAB308]/[0.06] px-3 py-2.5">
          <p className="text-xs text-[#EAB308]">Once verified &amp; SSL is active, this domain serves every selected usage — Partner Portal, Tracking and Postbacks. Test the portal via the Preview URL (<code>{previewHost ? `${previewHost}/portal/{slug}` : "/portal/{slug}"}</code>) before production.</p>
        </div>
      </div>
    </FormDrawer>

      <Dialog open={!!pendingTd} onOpenChange={(o) => { if (!o) setPendingTd(null); }}>
        <DialogContent className="border-white/[0.1] bg-[#141417]" data-testid="td-default-dialog">
          <DialogHeader>
            <DialogTitle>Make default tracking domain</DialogTitle>
            <DialogDescription>Choose how <b>{domain.domain}</b> becomes your default tracking domain. Nothing is saved until you confirm.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {[["new_offers", "Only for new GEO Offers", "New offers inherit this domain. Existing offers and streams are not changed. (Safe)"],
              ["org_wide", "For the entire organization", "Repoint every existing GEO Offer that used the old default to this domain. Streams that inherited it follow automatically; manual overrides stay."]].map(([v, t, d]) => (
              <label key={v} className={cn("block cursor-pointer rounded-lg border p-3", pendingTd === v ? "border-white/30 bg-white/[0.04]" : "border-white/10")}>
                <div className="flex items-center gap-2">
                  <input type="radio" name="td-mode-drawer" checked={pendingTd === v} onChange={() => setPendingTd(v)} data-testid={`td-mode-${v}`} className="accent-white" />
                  <span className="text-sm font-medium text-foreground">{t}</span>
                </div>
                <p className="mt-1 pl-6 text-xs text-muted-foreground">{d}</p>
              </label>
            ))}
            {pendingTd === "org_wide" && (
              <p className="rounded-md border border-[#EAB308]/30 bg-[#EAB308]/10 p-2.5 text-xs text-[#EAB308]" data-testid="td-org-warning">
                This will update all GEO Offers and related Streams currently using the default domain.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingTd(null)} data-testid="td-cancel" className="border-white/15">Cancel</Button>
            <Button onClick={confirmTd} disabled={saving} data-testid="td-confirm" className="bg-white text-black hover:bg-white/90">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function CnameInstructions({ domain, cname }) {
  const target = cname || "trk.tracify.top";
  const { host, root, isRoot, valid } = cnameParts(domain);
  const Row = ({ k, v }) => (
    <div className="flex items-center justify-between rounded-lg border border-white/[0.08] bg-[#141417] px-3 py-2 font-mono text-xs">
      <span className="text-muted-foreground">{k}</span>
      <span className="flex items-center gap-2 text-foreground">{v}<button onClick={() => copyText(v)} className="text-muted-foreground hover:text-foreground"><Copy className="h-3 w-3" /></button></span>
    </div>
  );
  return (
    <div className="space-y-3 rounded-xl border border-white/[0.08] bg-[#0A0A0A] p-4" data-testid="cname-instructions">
      <h4 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Connect via CNAME</h4>
      {domain && isRoot ? (
        <p className="rounded-lg border border-[#EAB308]/20 bg-[#EAB308]/[0.06] px-3 py-2 text-xs text-[#EAB308]">
          Root domains usually cannot use CNAME. Use a subdomain like <span className="text-foreground">track.{root}</span>, <span className="text-foreground">go.{root}</span> or <span className="text-foreground">partners.{root}</span>.
        </p>
      ) : (
        <div className="space-y-2">
          <Row k="Type" v="CNAME" />
          <Row k="Host" v={valid ? host : "track"} />
          <Row k="Target" v={target} />
        </div>
      )}
      <p className="text-xs text-muted-foreground">Point <span className="text-foreground">{domain || "your subdomain"}</span> at <span className="text-foreground">{target}</span>. Verification &amp; SSL are issued automatically after the record propagates.</p>
    </div>
  );
}
